import Mustache from 'mustache';
import galleryModalHtml from './_modal.html';
import './style.scss';

/**
 * 共通画像モーダル（間取り・外観・一覧など）
 * Figma PC 5478:33494 / 5531:27654 ・ SP 5168:73612 / 5168:58228
 */
class GalleryModal {
    constructor(params = {}) {
        this.settings = {
            ...this.defaults(),
            ...params,
        };
        this.index = 0;
        this.root = null;
        this.init();
    }

    defaults() {
        return {
            imgList: [],
            overlayClose: true,
            contactUrl: '',
            visitUrl: '',
            mode: 'single',
        };
    }

    init() {
        document.querySelectorAll('.plugin-gallery-modal').forEach((el) => {
            el.remove();
        });

        const wrap = document.createElement('div');
        wrap.innerHTML = Mustache.render(galleryModalHtml, {});
        this.root = wrap.firstElementChild;
        document.body.appendChild(this.root);

        this.imgEl = this.root.querySelector('.plugin-gallery-modal__img');
        this.captionEl = this.root.querySelector('[data-gallery-caption]');
        this.prevBtn = this.root.querySelector('[data-gallery-prev]');
        this.nextBtn = this.root.querySelector('[data-gallery-next]');
        this.stageEl = this.root.querySelector('.plugin-gallery-modal__stage');
        this.scrollerEl = this.root.querySelector('[data-gallery-scroller]');
        this.ctasEl = this.root.querySelector('[data-gallery-ctas]');
        this.contactLink = this.root.querySelector('[data-gallery-contact]');
        this.visitLink = this.root.querySelector('[data-gallery-visit]');

        this.root.querySelectorAll('[data-gallery-close]').forEach((el) => {
            if (!this.settings.overlayClose && el.classList.contains('plugin-gallery-modal__overlay')) {
                return;
            }
            el.addEventListener('click', () => this.close());
        });

        this.prevBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.show(this.index - 1);
        });
        this.nextBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.show(this.index + 1);
        });

        this.setupSwipe();
        this.setupHorizontalScroll();

        this._onKey = (e) => {
            if (!this.root.classList.contains('is-show')) return;
            if (e.key === 'Escape') this.close();
            if (e.key === 'ArrowLeft') this.show(this.index - 1);
            if (e.key === 'ArrowRight') this.show(this.index + 1);
        };
        document.addEventListener('keydown', this._onKey);

        this.setupCtas();
    }

    setupSwipe() {
        const stage = this.stageEl;
        if (!stage) return;
        if (this.isListMode) return;

        this._swipeAbort = new AbortController();
        const { signal } = this._swipeAbort;

        const SWIPE_THRESHOLD = 50;
        const AXIS_LOCK_PX = 10;
        let startX = 0;
        let startY = 0;
        let pointerId = null;
        let axisLocked = null;

        const resetPointer = () => {
            if (pointerId !== null && stage.hasPointerCapture?.(pointerId)) {
                stage.releasePointerCapture?.(pointerId);
            }
            pointerId = null;
            axisLocked = null;
            stage.classList.remove('is-dragging');
        };

        stage.addEventListener('pointerdown', (e) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            if (e.target.closest('[data-gallery-prev], [data-gallery-next], [data-gallery-close]')) return;

            pointerId = e.pointerId;
            startX = e.clientX;
            startY = e.clientY;
            axisLocked = null;
        }, { signal });

        stage.addEventListener('pointermove', (e) => {
            if (e.pointerId !== pointerId) return;

            const dx = e.clientX - startX;
            const dy = e.clientY - startY;

            if (!axisLocked) {
                if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
                axisLocked = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
                if (axisLocked === 'x' && this.list.length > 1) {
                    stage.setPointerCapture?.(pointerId);
                }
            }

            if (axisLocked === 'x' && this.list.length > 1) {
                stage.classList.add('is-dragging');
                e.preventDefault();
            }
        }, { passive: false, signal });

        const onPointerEnd = (e) => {
            if (e.pointerId !== pointerId) return;

            const dx = e.clientX - startX;
            if (axisLocked === 'x' && this.list.length > 1 && Math.abs(dx) >= SWIPE_THRESHOLD) {
                this.show(this.index + (dx < 0 ? 1 : -1));
            }

            resetPointer();
        };

        stage.addEventListener('pointerup', onPointerEnd, { signal });
        stage.addEventListener('pointercancel', onPointerEnd, { signal });
        stage.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
    }

    setupHorizontalScroll() {
        const scroller = this.scrollerEl;
        if (!scroller) return;

        this._scrollAbort = new AbortController();
        const { signal } = this._scrollAbort;
        const DRAG_THRESHOLD = 5;
        let pointerId = null;
        let startX = 0;
        let startScrollLeft = 0;
        let dragDistance = 0;

        const resetDrag = () => {
            if (pointerId !== null && scroller.hasPointerCapture?.(pointerId)) {
                scroller.releasePointerCapture?.(pointerId);
            }
            pointerId = null;
            startX = 0;
            startScrollLeft = 0;
            dragDistance = 0;
            scroller.classList.remove('is-dragging');
        };

        scroller.addEventListener('wheel', (e) => {
            if (!this.isListMode || scroller.scrollWidth <= scroller.clientWidth) return;

            const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
            if (delta === 0) return;

            e.preventDefault();
            scroller.scrollLeft += delta;
        }, { passive: false, signal });

        scroller.addEventListener('scroll', () => {
            if (!this.isListMode) return;
            this.syncIndexFromScroll();
        }, { passive: true, signal });

        scroller.addEventListener('pointerdown', (e) => {
            if (!this.isListMode || scroller.scrollWidth <= scroller.clientWidth) return;
            if (e.pointerType === 'mouse' && e.button !== 0) return;

            pointerId = e.pointerId;
            startX = e.clientX;
            startScrollLeft = scroller.scrollLeft;
            dragDistance = 0;
            scroller.setPointerCapture?.(pointerId);
        }, { signal });

        scroller.addEventListener('pointermove', (e) => {
            if (e.pointerId !== pointerId) return;

            const dx = e.clientX - startX;
            dragDistance = Math.max(dragDistance, Math.abs(dx));
            if (dragDistance < DRAG_THRESHOLD) return;

            e.preventDefault();
            scroller.classList.add('is-dragging');
            scroller.scrollLeft = startScrollLeft - dx;
        }, { passive: false, signal });

        const onPointerEnd = (e) => {
            if (e.pointerId !== pointerId) return;
            if (dragDistance >= DRAG_THRESHOLD) {
                this.ignoreScrollerClick = true;
                window.setTimeout(() => {
                    this.ignoreScrollerClick = false;
                }, 0);
            }
            resetDrag();
        };

        scroller.addEventListener('pointerup', onPointerEnd, { signal });
        scroller.addEventListener('pointercancel', onPointerEnd, { signal });
        scroller.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
    }

    setupCtas() {
        const contactUrl = this.settings.contactUrl
            || document.querySelector('.c-others-search-submit a[data-type="contact"]')?.getAttribute('href')
            || '';
        const visitUrl = this.settings.visitUrl
            || document.querySelector('.c-others-search-submit a[data-type="visit"]')?.getAttribute('href')
            || '';

        if (!contactUrl && !visitUrl) {
            this.ctasEl.hidden = true;
            return;
        }

        this.ctasEl.hidden = false;
        if (contactUrl) {
            this.contactLink.href = contactUrl;
            this.contactLink.hidden = false;
        } else {
            this.contactLink.hidden = true;
        }
        if (visitUrl) {
            this.visitLink.href = visitUrl;
            this.visitLink.hidden = false;
        } else {
            this.visitLink.hidden = true;
        }
    }

    get list() {
        return Array.isArray(this.settings.imgList) ? this.settings.imgList : [];
    }

    get isListMode() {
        return this.settings.mode === 'list';
    }

    normalizeItem(item) {
        if (typeof item === 'string') {
            return { src: item, alt: '' };
        }

        return {
            src: item?.src || item?.imagePath || '',
            alt: item?.alt || item?.comment || item?.title || '',
        };
    }

    render() {
        const list = this.list;
        if (!list.length) return;

        this.root.classList.toggle('is-list-mode', this.isListMode);

        if (this.isListMode) {
            this.renderScroller();
        } else {
            const item = this.normalizeItem(list[this.index] || list[0]);
            this.imgEl.src = item.src;
            this.imgEl.alt = item.alt;
        }

        const item = this.normalizeItem(list[this.index] || list[0]);
        const alt = item.alt;
        if (this.isListMode || !alt) {
            this.captionEl.textContent = '';
            this.captionEl.hidden = true;
        } else {
            this.captionEl.textContent = alt;
            this.captionEl.hidden = false;
        }

        const multi = list.length > 1;
        this.prevBtn.hidden = !multi;
        this.nextBtn.hidden = !multi;
        this.stageEl?.classList.toggle('is-swipeable', multi && !this.isListMode);
        this.stageEl?.classList.remove('is-dragging');
    }

    renderScroller() {
        if (!this.scrollerEl) return;
        if (this.scrollerEl.childElementCount === this.list.length) {
            this.scrollerEl.querySelectorAll('.plugin-gallery-modal__scroll-figure').forEach((figure, index) => {
                figure.classList.toggle('is-current', index === this.index);
            });
            return;
        }

        this.scrollerEl.replaceChildren();
        this.list.forEach((rawItem, index) => {
            const item = this.normalizeItem(rawItem);
            if (!item.src) return;

            const figure = document.createElement('figure');
            figure.className = 'plugin-gallery-modal__scroll-figure';
            figure.classList.toggle('is-current', index === this.index);

            const img = document.createElement('img');
            img.className = 'plugin-gallery-modal__scroll-img';
            img.src = item.src;
            img.alt = item.alt;
            img.loading = index === 0 ? 'eager' : 'lazy';
            img.draggable = false;

            figure.appendChild(img);

            if (item.alt) {
                const caption = document.createElement('figcaption');
                caption.className = 'plugin-gallery-modal__scroll-caption';
                caption.textContent = item.alt;
                figure.appendChild(caption);
            }

            figure.addEventListener('click', (e) => {
                if (this.ignoreScrollerClick) {
                    e.preventDefault();
                    return;
                }
                this.show(index);
            });
            this.scrollerEl.appendChild(figure);
        });
    }

    scrollToIndex({ smooth = false } = {}) {
        if (!this.isListMode || !this.scrollerEl) return;

        const target = this.scrollerEl.children[this.index];
        if (!target) return;

        const left = target.offsetLeft - ((this.scrollerEl.clientWidth - target.clientWidth) / 2);
        const max = Math.max(0, this.scrollerEl.scrollWidth - this.scrollerEl.clientWidth);
        this.scrollerEl.scrollTo({
            left: Math.min(Math.max(0, left), max),
            behavior: smooth ? 'smooth' : 'auto',
        });
    }

    syncIndexFromScroll() {
        if (!this.scrollerEl?.children.length) return;

        const center = this.scrollerEl.scrollLeft + (this.scrollerEl.clientWidth / 2);
        let nextIndex = this.index;
        let closest = Infinity;

        Array.from(this.scrollerEl.children).forEach((figure, index) => {
            const figureCenter = figure.offsetLeft + (figure.clientWidth / 2);
            const distance = Math.abs(figureCenter - center);
            if (distance < closest) {
                closest = distance;
                nextIndex = index;
            }
        });

        if (nextIndex === this.index) return;
        this.index = nextIndex;
        this.render();
    }

    show(index = 0) {
        const list = this.list;
        if (!list.length) return;

        const len = list.length;
        this.index = ((index % len) + len) % len;
        this.render();
        this.root.classList.add('is-show');
        if (this.isListMode) {
            window.requestAnimationFrame(() => this.scrollToIndex({ smooth: true }));
        }
        document.querySelector('html')?.classList.add('is-fixed');
    }

    close() {
        this.stageEl?.classList.remove('is-dragging', 'is-swipeable');
        this.root?.classList.remove('is-show');
        document.querySelector('html')?.classList.remove('is-fixed');
    }

    destroy() {
        this._swipeAbort?.abort();
        this._swipeAbort = null;
        this._scrollAbort?.abort();
        this._scrollAbort = null;
        document.removeEventListener('keydown', this._onKey);
        this.close();
        this.root?.remove();
        this.root = null;
    }
}

export default GalleryModal;
